import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../services/api";
import { format, addDays, subDays, parseISO } from "date-fns";
import "./CashReconciliationPage.css";


const CashReconciliationPage = () => {
  const { id: businessId } = useParams();
  const [categories, setCategories] = useState([]);
  const [entryData, setEntryData] = useState({});
  const [cashAdvanceData, setCashAdvanceData] = useState({});
  const [currentDate, setCurrentDate] = useState(new Date());

  const navigate = useNavigate();


  const daysToShow = [-2, -1, 0, 1, 2].map(offset =>
    format(addDays(currentDate, offset), "yyyy-MM-dd")
  );

  const loadCategories = useCallback(async () => {
    try {
      const { data } = await api.get(`/cash-register-categories`, {
        params: { business_id: businessId },
      });
      setCategories(data);
    } catch (err) {
      console.error("Error fetching categories:", err);
      alert("Failed to load categories");
    }
  }, [businessId]);

  const loadEntries = useCallback(async () => {
    try {
      const startDate = format(addDays(currentDate, -2), "yyyy-MM-dd");
      const endDate = format(addDays(currentDate, 2), "yyyy-MM-dd");

      const { data } = await api.get("/cash-register-entries", {
        params: {
          business_id: businessId,
          start_date: startDate,
          end_date: endDate,
        },
      });

      const newEntryData = {};
      const newCashAdvanceData = {};

      data.forEach(entry => {
        const dateKey = format(new Date(entry.entry_date), "yyyy-MM-dd");
        const key = `${entry.category_id}_${dateKey}`;

        if (entry.category_name === "Cash Advance") {
          if (!newCashAdvanceData[dateKey]) newCashAdvanceData[dateKey] = [];
          newCashAdvanceData[dateKey].push(entry);
        } else {
          newEntryData[key] = { id: entry.id, amount: entry.amount };
        }
      });

      setEntryData(newEntryData);
      setCashAdvanceData(newCashAdvanceData);
    } catch (err) {
      console.error("Error loading entries:", err);
      alert("Failed to load entries");
    }
  }, [businessId, currentDate]);

  useEffect(() => {
    loadCategories();
    loadEntries();
  }, [loadCategories, loadEntries]);

  const grouped = categories.reduce((acc, cat) => {
    if (!acc[cat.category_type]) acc[cat.category_type] = [];
    acc[cat.category_type].push(cat);
    return acc;
  }, {});

  const computeTypeTotalsByDate = () => {
    const totals = {};

    ["income", "non-sale", "payment", "paidouts", "refunds"].forEach(type => {
      totals[type] = {};
      daysToShow.forEach(date => {
        totals[type][date] = 0;

        const typeCats = grouped[type] || [];
        typeCats.forEach(cat => {
          if (cat.name === "Cash Advance") return;

          const key = `${cat.id}_${date}`;
          const val = parseFloat(entryData[key]?.amount || 0);
          if (!isNaN(val)) {
            totals[type][date] += val;
          }
        });

        if (type === "paidouts") {
          const caRows = cashAdvanceData[date] || [];
          caRows.forEach(row => {
            const val = parseFloat(row.amount || 0);
            if (!isNaN(val)) totals[type][date] += val;
          });
        }
      });
    });

    totals["till"] = {};
    daysToShow.forEach(date => {
      const openCat = grouped["till"]?.find(c => c.name === "Open Cash");
      const closeCat = grouped["till"]?.find(c => c.name === "Close Cash");
      const openKey = openCat ? `${openCat.id}_${date}` : null;
      const closeKey = closeCat ? `${closeCat.id}_${date}` : null;
      const openVal = openKey ? parseFloat(entryData[openKey]?.amount || 0) : 0;
      const closeVal = closeKey ? parseFloat(entryData[closeKey]?.amount || 0) : 0;
      totals["till"][date] = openVal - closeVal;
    });

    totals["required_cash"] = {};
    daysToShow.forEach(date => {
      const till = totals["till"]?.[date] || 0;
      const income = totals["income"]?.[date] || 0;
      const payment = totals["payment"]?.[date] || 0;
      const refund = totals["refunds"]?.[date] || 0;
      const paidouts = totals["paidouts"]?.[date] || 0;
      totals["required_cash"][date] = till + income - payment - refund - paidouts;
    });

    return totals;
  };

  const typeTotals = computeTypeTotalsByDate();

  const goLeft = () => setCurrentDate(prev => subDays(prev, 1));
  const goRight = () => setCurrentDate(prev => addDays(prev, 1));

  return (
    <div className="cash-reconciliation-page">
      <h2>Cash Reconciliation</h2>
      <div className="recon-btns">
        <button onClick={goLeft}>⬅️</button>
        <button onClick={goRight} style={{ marginLeft: 8 }}>➡️</button>
      </div>

      <div className="entries-grid" style={{ overflowX: "auto" }}>
        <table className="cash-recon-table">
          <thead>
            <tr>
              <th>Category</th>
              {daysToShow.map(date => (
                <th key={date}>{format(parseISO(date), "MMM d")}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {["till", "income", "non-sale", "payment", "paidouts", "refunds"].map(type =>
              grouped[type]?.map(cat => (
                <tr key={cat.id}>
                  <td>
                    <>{cat.name}</>
                    {cat.name !== "Open Cash" && cat.name !== "Close Cash" && (
                      <button
                        title="Reconcile"
                        onClick={() => navigate(`/dashboard/business/${businessId}/reconciliation/${cat.id}`)}
                      >
                        🔄
                      </button>
                    )}
                  </td>
                  {daysToShow.map(date => {
                    let displayValue = "";
                    if (cat.name === "Cash Advance") {
                      const caRows = cashAdvanceData[date] || [];
                      const sum = caRows.reduce((acc, row) => acc + parseFloat(row.amount || 0), 0);
                      displayValue = `$${sum.toFixed(2)}`;
                    } else {
                      const savedEntry = entryData[`${cat.id}_${date}`];
                      const amount = parseFloat(savedEntry?.amount || 0);
                      displayValue = `$${amount.toFixed(2)}`;

                    }

                    return <td key={`${cat.id}_${date}`}>{displayValue}</td>;
                  })}
                </tr>
              ))
            )}
          </tbody>
          <tfoot>
  <tr>
    <td>
      <strong>Required Cash</strong>
      <button
        title="Reconcile Required Cash"
        onClick={() => navigate(`/dashboard/business/${businessId}/required-cash`)}

      >
        🔄
      </button>
    </td>
    {daysToShow.map(date => (
      <td key={`required_cash_${date}`}>
        ${Number(typeTotals?.["required_cash"]?.[date] || 0).toFixed(2)}
      </td>
    ))}
  </tr>
</tfoot>

        </table>
      </div>
    </div>
  );
};

export default CashReconciliationPage;
